import React, { Fragment } from 'react';
import type { MainContentProps } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types.tsx';
import { IssueLayoutRequestTypeOwnerType } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/issue/issue-layout-owner.tsx';
import { DescriptionInlineInputTmp } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/description-inline-input-tmp/index.tsx';
import { FieldCategory } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/ui/field-category/index.tsx';
import {
	POSITION_ISSUE_TYPE_DESCRIPTION_INPUT,
	POSITION_ISSUE_TYPE_HELP_TEXT_INPUT,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/utils/validation/constants.tsx';
import {
	validateIssueTypeDescription,
	validateIssueTypeHelpText,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/utils/validation/issue-type/index.tsx';
import { FormPanelWithErrorBoundary as FormPanel } from '@atlassian/jira-project-settings-product-features-jsm-common/src/ui/form-panel/index.tsx';
import { JSD_IS_EMAIL_REQUEST_TYPE } from '../../common/constants.tsx';
import { getRequestFieldCategoryId } from '../../constants.tsx';
import {
	descriptionInlineInputMessages,
	validationMessages,
	helpTextInlineInputMessages,
} from './messages.tsx';

export const MainContent = ({
	issueTypeResetCount,
	activeFieldListRefElement,
	activeFieldCategories,
	children,
	fieldsDocumentationUrl,
	issueLayoutOwners,
	extraDefinerId,
	onUpdateIssueLayoutOwner,
	onSetHasSeenEmptyStatePulse,
	projectKey,
}: MainContentProps) => {
	const issueType = issueLayoutOwners.find(({ data: { id } }) => id === extraDefinerId);
	if (!issueType || issueType.type !== IssueLayoutRequestTypeOwnerType) {
		throw new Error("Can't find current request type data");
	}

	const { description, properties } = issueType.data;
	const isEmailRequestType = properties && properties[JSD_IS_EMAIL_REQUEST_TYPE];

	const onDescriptionUpdate = (value: string) => {
		onUpdateIssueLayoutOwner({
			...issueType,
			data: {
				...issueType.data,
				description: value,
			},
		});
	};

	const validateDescription = (value: string) =>
		validateIssueTypeDescription(value, validationMessages).find(
			({ position }) => position === POSITION_ISSUE_TYPE_DESCRIPTION_INPUT,
		);

	const onHelpTextUpdate = (value: string) => {
		onUpdateIssueLayoutOwner({
			...issueType,
			data: {
				...issueType.data,
				instructions: value,
			},
		});
	};

	const validateHelpText = (value: string) =>
		validateIssueTypeHelpText(value, validationMessages).find(
			({ position }) => position === POSITION_ISSUE_TYPE_HELP_TEXT_INPUT,
		);

	return (
		<>
			{!isEmailRequestType && (
				<>
					<DescriptionInlineInputTmp
						messages={descriptionInlineInputMessages}
						value={description}
						issueTypeResetCount={issueTypeResetCount}
						onChange={onDescriptionUpdate}
						onValidate={validateDescription}
					/>
					<DescriptionInlineInputTmp
						value={issueType.data.instructions}
						messages={helpTextInlineInputMessages}
						issueTypeResetCount={issueTypeResetCount}
						onChange={onHelpTextUpdate}
						onValidate={validateHelpText}
					/>
				</>
			)}
			{activeFieldListRefElement}
			{activeFieldCategories.map((category, index) => {
				const showFormPanel =
					!isEmailRequestType && category.configuration.name === getRequestFieldCategoryId();
				return (
					// The name is a unique identifier
					<Fragment key={category.configuration.name}>
						<FieldCategory
							key={category.configuration.name}
							categoryIndex={index}
							category={category}
							fieldsDocumentationUrl={fieldsDocumentationUrl}
						>
							{children({ category })}
						</FieldCategory>

						{showFormPanel && (
							<FormPanel
								category={category}
								projectKey={projectKey}
								onSetHasSeenEmptyStatePulse={onSetHasSeenEmptyStatePulse}
							/>
						)}
					</Fragment>
				);
			})}
		</>
	);
};
